import { Info } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
//  Single source of truth for the recommended upload dimensions surfaced to
//  users in every avatar / cover upload control across the app (teams,
//  communities, profile, game). Detail pages render the avatar as a 128px
//  circle and the banner at 200px tall × full width (≈ 4:1). Source assets
//  are sized at 4× for retina + future-proofing.
// ─────────────────────────────────────────────────────────────────────────────
export const RECOMMENDED_UPLOAD_SIZES = {
  avatar: { width: 512, height: 512, aspect: '1:1' },
  banner: { width: 1600, height: 400, aspect: '4:1' },
} as const

type UploadKind = keyof typeof RECOMMENDED_UPLOAD_SIZES

const COPY: Record<UploadKind, { label: string; recommended: string; tip: string }> = {
  avatar: {
    label: 'Profile picture',
    recommended: '512 × 512 px',
    tip: 'Square image (1:1). Renders as a 128 px circle. Use ≥ 512 px for crisp retina display.',
  },
  banner: {
    label: 'Banner',
    recommended: '1600 × 400 px',
    tip: 'Wide image (4:1). Renders at 200 px tall × full width. Crops from the center — keep important detail centered.',
  },
}

interface UploadHintProps {
  type: UploadKind
  className?: string
}

export const UploadHint = ({ type, className = '' }: UploadHintProps) => {
  const { label, recommended, tip } = COPY[type]
  return (
    <div
      className={`group relative inline-flex items-center gap-1.5 text-[11px] leading-tight text-white/55 ${className}`}
      role="note"
      aria-label={`${label} upload guidelines`}
    >
      <Info className="w-3 h-3 text-[#02f305]/85 shrink-0" strokeWidth={2.4} />
      <span className="font-mono uppercase tracking-[0.16em] text-white/45">
        {label}
      </span>
      <span className="text-white/30">·</span>
      <span className="text-white/80 font-medium tabular-nums">{recommended}</span>

      {/* hover tooltip with the long-form tip */}
      <div
        className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 w-[260px] rounded-md border border-white/10 bg-black/95 px-3 py-2 text-[11px] leading-snug text-white/80 opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.6)] transition-opacity duration-150 group-hover:opacity-100"
        role="tooltip"
      >
        {tip}
      </div>
    </div>
  )
}

export default UploadHint
